/**
 * Test-only reCAPTCHA Verifier for Cypress E2E Testing
 *
 * This module provides a mock ApplicationVerifier that bypasses the reCAPTCHA UI
 * when running in Cypress. It implements Firebase's ApplicationVerifier interface
 * and returns a dummy token that Firebase accepts for test phone numbers.
 *
 * SECURITY NOTE:
 * - This mock ONLY works with Firebase test phone numbers configured in the Firebase Console
 * - Real phone numbers will still fail verification with this mock
 * - Only activates when Cypress is detected (window.Cypress)
 */
/**
 * Detects if we're running in Cypress.
 */
function isCypressEnvironment() {
    return typeof window !== 'undefined' && 'Cypress' in window;
}
/**
 * Mock ApplicationVerifier for E2E testing.
 *
 * Implements Firebase's ApplicationVerifier interface with a dummy token.
 * Firebase accepts this for test phone numbers (configured in Firebase Console)
 * because test numbers bypass actual reCAPTCHA validation.
 *
 * IMPORTANT: This also implements internal Firebase SDK methods like _reset()
 * that are called by _verifyPhoneNumber() but aren't part of the public interface.
 *
 * DO NOT use this in production - it will fail for real phone numbers.
 */
class TestRecaptchaVerifier {
    constructor() {
        /**
         * The type of verifier - Firebase expects 'recaptcha'
         */
        this.type = 'recaptcha';
        /**
         * Internal widget ID - Firebase SDK may reference this
         */
        this._widgetId = 0;
    }
    /**
     * Returns a dummy verification token.
     * For Firebase test phone numbers, this token is accepted without validation.
     *
     * Note: The token format mimics a real reCAPTCHA token to satisfy any
     * client-side validation in the Firebase SDK.
     *
     * Note: Using `async` here (even without await) matches the admin_portal implementation
     * and ensures consistent behavior with Firebase's ApplicationVerifier interface.
     * The linter warning is acceptable for this test utility.
     */
    // eslint-disable-next-line @typescript-eslint/require-await -- must match Firebase ApplicationVerifier async interface
    async verify() {
        console.log('[E2E] TestRecaptchaVerifier.verify() called - returning mock token');
        // Return a token that looks more like a real reCAPTCHA token
        // Firebase test phone numbers should accept any token, but some SDK
        // versions may do basic format validation
        return '03AFcWeA5' + 'x'.repeat(500) + '_e2e_test_token';
    }
    /**
     * Internal Firebase SDK method - called after verification to reset the widget.
     * This is NOT part of the public ApplicationVerifier interface but is called
     * internally by Firebase's _verifyPhoneNumber function.
     */
    _reset() {
        console.log('[E2E] TestRecaptchaVerifier._reset() called');
        // No-op for mock - nothing to reset
    }
    /**
     * Clears the verifier - public method for cleanup
     */
    clear() {
        console.log('[E2E] TestRecaptchaVerifier.clear() called');
        this._widgetId = null;
    }
    /**
     * Renders the verifier - returns widget ID
     * Some code paths may call this expecting a promise
     */
    render() {
        console.log('[E2E] TestRecaptchaVerifier.render() called');
        return Promise.resolve(this._widgetId ?? 0);
    }
}
/**
 * Creates a mock ApplicationVerifier if running in Cypress.
 *
 * @returns TestRecaptchaVerifier if in Cypress, null otherwise
 */
function createTestVerifierIfCypress() {
    if (isCypressEnvironment()) {
        console.log('[Cypress] Using mock reCAPTCHA verifier');
        return new TestRecaptchaVerifier();
    }
    return null;
}

export { TestRecaptchaVerifier, createTestVerifierIfCypress, isCypressEnvironment };
//# sourceMappingURL=testRecaptchaVerifier.js.map
